import { useMemo } from 'react'
import type { PurchasingPurchaseLineRow } from '@/services/purchasingTypes'
import { formatUsd } from '@/components/gcp/gcpHelpers'
import { buildPriceSparklinePoints } from '@/pages/purchasing/purchasingPriceSparklineData'

const SPARKLINE_COLOR = '#2563eb'
const GRID_LINE_COLOR = '#e5e7eb'
const AXIS_TEXT_CLASS = 'fill-gray-500 text-[10px]'

interface PurchasingPriceSparklineProps {
  lines: PurchasingPurchaseLineRow[]
  className?: string
}

export default function PurchasingPriceSparkline({
  lines,
  className = '',
}: PurchasingPriceSparklineProps) {
  const points = useMemo(() => buildPriceSparklinePoints(lines), [lines])

  if (points.length === 0) {
    return <p className="text-sm text-gray-500">No priced lines in the last 90 days.</p>
  }

  const width = 480
  const height = 120
  const margin = { top: 12, right: 12, bottom: 28, left: 48 }
  const innerWidth = width - margin.left - margin.right
  const innerHeight = height - margin.top - margin.bottom

  const values = points.map((p) => p.value)
  const minValue = Math.min(...values)
  const maxValue = Math.max(...values)
  const valueSpan = maxValue - minValue || maxValue || 1

  const xStep = points.length > 1 ? innerWidth / (points.length - 1) : 0

  const pathD = points
    .map((point, index) => {
      const x = margin.left + index * xStep
      const y =
        margin.top +
        innerHeight -
        ((point.value - minValue) / valueSpan) * innerHeight
      return `${index === 0 ? 'M' : 'L'} ${x.toFixed(1)} ${y.toFixed(1)}`
    })
    .join(' ')

  const lastPoint = points[points.length - 1]!

  return (
    <div className={className}>
      <div className="mb-1 flex items-baseline justify-between gap-2 text-xs text-gray-500">
        <span>90-day cost per atomic unit</span>
        <span className="tabular-nums text-gray-700">
          Latest: {formatUsd(lastPoint.value)} · {lastPoint.date}
        </span>
      </div>
      <svg
        viewBox={`0 0 ${width} ${height}`}
        className="w-full max-w-full"
        role="img"
        aria-label="90-day price sparkline"
      >
        <line
          x1={margin.left}
          y1={margin.top + innerHeight}
          x2={width - margin.right}
          y2={margin.top + innerHeight}
          stroke={GRID_LINE_COLOR}
          strokeWidth={1}
        />
        <text
          x={margin.left - 6}
          y={margin.top + 10}
          className={AXIS_TEXT_CLASS}
          textAnchor="end"
        >
          {formatUsd(maxValue)}
        </text>
        <text
          x={margin.left - 6}
          y={margin.top + innerHeight}
          className={AXIS_TEXT_CLASS}
          textAnchor="end"
        >
          {formatUsd(minValue)}
        </text>
        <path
          d={pathD}
          fill="none"
          stroke={SPARKLINE_COLOR}
          strokeWidth={2}
          strokeLinejoin="round"
          strokeLinecap="round"
        />
        {points.map((point, index) => {
          const x = margin.left + index * xStep
          const y =
            margin.top +
            innerHeight -
            ((point.value - minValue) / valueSpan) * innerHeight
          return (
            <circle
              key={`${point.date}-${index}`}
              cx={x}
              cy={y}
              r={3}
              fill={SPARKLINE_COLOR}
            />
          )
        })}
      </svg>
    </div>
  )
}
